/**
 * Enquiry modal.
 *
 * Opens the lead-capture modal (a native <dialog>) from any `[data-enquiry-trigger]` element. Closing is native:
 * the close button is a `<form method="dialog">` submit and Escape works for free.
 *
 * One modal serves every call to action on the site, so each trigger describes itself through `data-enquiry-type`
 * and `data-enquiry-subject`. Those pick the heading and fill the form's hidden fields, telling an enquiry apart from
 * the deal, service or general button that raised it.
 */
function initEnquiryModal(): void {
	const modal = document.getElementById('enquiry-modal') as HTMLDialogElement | null;
	if (!modal) {
		return;
	}

	const heading = modal.querySelector<HTMLElement>('#enquiry-modal-title');
	const typeField = modal.querySelector<HTMLInputElement>('.wcwa-enquiry-type input');
	const subjectField = modal.querySelector<HTMLInputElement>('.wcwa-enquiry-subject input');

	document.querySelectorAll<HTMLElement>('[data-enquiry-trigger]').forEach((trigger) => {
		trigger.addEventListener('click', (event) => {
			event.preventDefault();

			/* Every open rewrites all three, so a deal clicked earlier cannot leak into an unrelated enquiry. */
			const type = trigger.dataset.enquiryType ?? 'General';
			const subject = trigger.dataset.enquirySubject ?? '';

			if (typeField) {
				typeField.value = type;
			}
			if (subjectField) {
				subjectField.value = subject;
			}
			if (heading) {
				heading.textContent =
					modal.dataset[`title${type}`] ?? modal.dataset.titleGeneral ?? heading.textContent;
			}

			modal.showModal();

			/* Land focus in the form on pointer devices; touch screens keep the default (the close button) so the
			   keyboard stays down until the visitor taps a field. */
			if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
				modal.querySelector<HTMLInputElement>('input:not([type="hidden"])')?.focus();
			}
		});
	});

	// Click on the backdrop (the dialog element itself, outside its content) closes it.
	modal.addEventListener('click', (event) => {
		if (event.target === modal) {
			modal.close();
		}
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initEnquiryModal);
} else {
	initEnquiryModal();
}
